import React, { useEffect, useState } from "react";
import styled from "styled-components";

/**
 * The author's quote window (backend aotdsys-1.2): the quote goes on the day's
 * prints, so it can only be set in the first 45 minutes after the art appears.
 * `/info` gives the author `quoteWindowEndsAt`; the backend refuses a quote
 * after it, this only keeps the page honest about it.
 *
 * - `useQuoteWindow` ticks every second while the window is open.
 * - `QuoteWindowCountdown` is the "mm:ss left" line over the quote form.
 */

export interface QuoteWindow {
  /** False once the window has closed. Open when the server sent no end (older backend). */
  open: boolean;
  /** Milliseconds left, or null when the server sent no end. */
  msLeft: number | null;
}

export function quoteWindowAt(endsAt: string | undefined, now: number): QuoteWindow {
  const end = endsAt ? Date.parse(endsAt) : NaN;
  if (!Number.isFinite(end)) return { open: true, msLeft: null };
  const msLeft = Math.max(0, end - now);
  return { open: msLeft > 0, msLeft };
}

export function useQuoteWindow(endsAt: string | undefined): QuoteWindow {
  const [now, setNow] = useState(() => Date.now());
  const state = quoteWindowAt(endsAt, now);
  const ticking = state.open && state.msLeft !== null;
  useEffect(() => {
    if (!ticking) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [ticking]);
  return state;
}

/** "44:59", or "0:07" near the end. */
export function formatLeft(ms: number): string {
  const s = Math.ceil(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

const Countdown = styled.div`
  margin-top: 14px;
  font-size: 0.85rem;
  color: #57534e;
  font-variant-numeric: tabular-nums;
  text-align: center;

  b {
    color: #222;
  }
`;

export const QuoteWindowCountdown: React.FC<{ msLeft: number | null }> = ({ msLeft }) =>
  msLeft === null ? null : (
    <Countdown role="timer" aria-live="off">
      <b>{formatLeft(msLeft)}</b> left to add your quote
    </Countdown>
  );
